import { Alert, Button, Col, Row } from 'reactstrap';
import React, { useCallback, useMemo, useState } from 'react';
import logo from '../assets/images/logo.png';
import Form from '../components/Form';
import { useForm } from 'react-hook-form';
import TextField from '../components/TextField';
import PasswordField from '../components/PasswordField';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useApiPost } from '../hooks/useApi';
import useFieldSetErrors from '../hooks/useFieldSetErrors';
import { Link, useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/useToast';

type Step = 'email' | 'code' | 'password';

const ForgotPasswordPage = () => {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const navigate = useNavigate();
  const { addToast } = useToast();

  // Step 1: Email form
  const emailSchema = useMemo(
    () =>
      yup
        .object({
          email: yup
            .string()
            .email('Invalid email')
            .required('Email is required')
        })
        .required(),
    []
  );
  const emailMethods = useForm({ resolver: yupResolver(emailSchema) });
  const emailOnError = useFieldSetErrors(emailMethods.setError);
  const { mutate: requestCode, isPending: isRequestingCode } = useApiPost(
    '/recover-password'
  );

  // Step 2: Code verification form
  const codeSchema = useMemo(
    () =>
      yup
        .object({
          code: yup
            .string()
            .required('Code is required')
            .length(6, 'Code must be 6 digits')
        })
        .required(),
    []
  );
  const codeMethods = useForm({ resolver: yupResolver(codeSchema) });
  const codeOnError = useFieldSetErrors(codeMethods.setError);
  const { mutate: verifyCode, isPending: isVerifyingCode } = useApiPost(
    '/verify-reset-code'
  );

  // Step 3: New password form
  const passwordSchema = useMemo(
    () =>
      yup
        .object({
          new_password: yup
            .string()
            .required('Password is required')
            .min(6, 'Password must be at least 6 characters'),
          confirm_password: yup
            .string()
            .required('Confirm password is required')
            .oneOf([yup.ref('new_password')], 'Passwords must match')
        })
        .required(),
    []
  );
  const passwordMethods = useForm({ resolver: yupResolver(passwordSchema) });
  const passwordOnError = useFieldSetErrors(passwordMethods.setError);
  const { mutate: resetPassword, isPending: isResettingPassword } = useApiPost(
    '/reset-password'
  );

  // Step 1 handler
  const handleRequestCode = useCallback(
    (data: any) => {
      requestCode(
        { email: data.email },
        {
          onError: emailOnError,
          onSuccess: () => {
            setEmail(data.email);
            setStep('code');
            addToast({
              color: 'success',
              message: 'Verification code sent to your email',
              icon: 'check'
            });
          }
        }
      );
    },
    [requestCode, emailOnError, addToast]
  );

  // Step 2 handler
  const handleVerifyCode = useCallback(
    (data: any) => {
      verifyCode(
        { email, code: data.code },
        {
          onError: codeOnError,
          onSuccess: () => {
            setCode(data.code);
            setStep('password');
          }
        }
      );
    },
    [verifyCode, email, codeOnError]
  );

  // Step 3 handler
  const handleResetPassword = useCallback(
    (data: any) => {
      resetPassword(
        {
          email,
          code,
          new_password: data.new_password,
          confirm_password: data.confirm_password
        },
        {
          onError: passwordOnError,
          onSuccess: () => {
            addToast({
              color: 'success',
              message: 'Password reset successfully! Please login.',
              icon: 'check'
            });
            navigate('/login');
          }
        }
      );
    },
    [resetPassword, email, code, passwordOnError, addToast, navigate]
  );

  // Resend code handler
  const handleResendCode = useCallback(() => {
    requestCode(
      { email },
      {
        onError: emailOnError,
        onSuccess: () => {
          addToast({
            color: 'success',
            message: 'New verification code sent to your email',
            icon: 'check'
          });
        }
      }
    );
  }, [requestCode, email, emailOnError, addToast]);

  return (
    <Row className="d-flex justify-content-center align-items-center vh-100">
      <Col md={4} className="mx-auto">
        <img src={logo} alt="logo" className="img-fluid" />

        {step === 'email' && (
          <>
            <p className="py-2 text-center">
              Enter your email address to recover your password
            </p>
            <Form onSubmit={handleRequestCode} {...emailMethods}>
              <TextField type="text" name="email" placeholder="Email" />
              <Button
                color={emailMethods.formState.isValid ? 'primary' : 'secondary'}
                size="lg"
                className="w-100"
                disabled={isRequestingCode}
              >
                {isRequestingCode ? 'Sending...' : 'Send Recovery Code'}
              </Button>
            </Form>
          </>
        )}

        {step === 'code' && (
          <>
            <p className="py-2 text-center">
              Enter the 6-digit code sent to <strong>{email}</strong>
            </p>
            <Form onSubmit={handleVerifyCode} {...codeMethods}>
              <TextField
                type="text"
                name="code"
                placeholder="Enter 6-digit code"
              />
              <Button
                color={codeMethods.formState.isValid ? 'primary' : 'secondary'}
                size="lg"
                className="w-100"
                disabled={isVerifyingCode}
              >
                {isVerifyingCode ? 'Verifying...' : 'Verify Code'}
              </Button>
            </Form>
            <p className="pt-3 text-center">
              <Button
                color="link"
                className="text-decoration-none p-0"
                onClick={handleResendCode}
                disabled={isRequestingCode}
              >
                Resend code
              </Button>
            </p>
          </>
        )}

        {step === 'password' && (
          <>
            <p className="py-2 text-center">Enter your new password</p>
            <Form onSubmit={handleResetPassword} {...passwordMethods}>
              <PasswordField
                name="new_password"
                placeholder="New Password"
                label=""
              />
              <PasswordField
                name="confirm_password"
                placeholder="Confirm New Password"
                label=""
              />
              <Button
                color={
                  passwordMethods.formState.isValid ? 'primary' : 'secondary'
                }
                size="lg"
                className="w-100"
                disabled={isResettingPassword}
              >
                {isResettingPassword ? 'Resetting...' : 'Reset Password'}
              </Button>
            </Form>
          </>
        )}

        <p className="pt-4">
          <Link to={'/login'} className="text-decoration-none small">
            Back to login
          </Link>
        </p>
      </Col>
    </Row>
  );
};

export default ForgotPasswordPage;
